import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { StoreBadge } from './StoreBadge';
import { nativeEntryLink, nativeEntryStore } from '../lib/nativeEntry';
import type { NativeEntryContext } from '../lib/nativeEntry';
import './native-entry.css';

export function NativeEntry({ context }: { context: NativeEntryContext }) {
	const { code, device, nativeReady } = context;
	const [redirect, setRedirect] = useState(() => nativeEntryStore(context));
	const [qr, setQr] = useState('');
	const [qrFailed, setQrFailed] = useState(false);
	const link = nativeEntryLink(code);
	useEffect(() => {
		// Same display and body faces as scandeckapp.com, loaded for this page only so
		// the rest of the app keeps its system font stack and offline behaviour.
		if (!document.getElementById('native-entry-fonts')) {
			const fonts = document.createElement('link');
			fonts.id = 'native-entry-fonts';
			fonts.rel = 'stylesheet';
			fonts.href = 'https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@500,700,800&f[]=satoshi@400,500,700&display=swap';
			document.head.appendChild(fonts);
		}
	}, []);
	useEffect(() => {
		document.title = 'ScanDeck Lead Capture';
		// Drop unknown parameters, fragments, PINs and old email tokens from this page.
		window.history.replaceState(null, '', `/open${code ? `?v=${encodeURIComponent(code)}` : ''}`);
		let alive = true;
		if (device === 'desktop') {
			void QRCode.toDataURL(link, { width: 240, margin: 2, color: { dark: '#0d1a3a', light: '#ffffff' } })
				.then(data => { if (alive) setQr(data); })
				.catch(() => { if (alive) setQrFailed(true); });
		}
		return () => { alive = false; };
	}, [code, device, link]);
	useEffect(() => {
		if (!redirect) return;
		const cancel = () => setRedirect(null);
		const hidden = () => { if (document.hidden) cancel(); };
		const timer = window.setTimeout(() => {
			if (!document.hidden) window.location.replace(redirect);
		}, 7000);
		window.addEventListener('pagehide', cancel);
		document.addEventListener('visibilitychange', hidden);
		document.addEventListener('pointerdown', cancel);
		document.addEventListener('keydown', cancel);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener('pagehide', cancel);
			document.removeEventListener('visibilitychange', hidden);
			document.removeEventListener('pointerdown', cancel);
			document.removeEventListener('keydown', cancel);
		};
	}, [redirect]);

	return (
		<div className="native-entry">
			<header className="native-entry-topbar">
				<div className="native-entry-topbar-inner">
					<a className="native-entry-brand" href="https://scandeckapp.com/" aria-label="ScanDeck home">
						<img src="/scandeck-logo-dark-tight.svg" alt="ScanDeck" width="210" height="48" />
					</a>
					<nav aria-label="ScanDeck site">
						<a href="https://scandeckapp.com/">Home</a>
						<a href="https://scandeckapp.com/how-it-works/">How It Works</a>
						<a href="https://scandeckapp.com/pricing/">Pricing</a>
						<a href="https://scandeckapp.com/contact/">Contact</a>
					</nav>
				</div>
			</header>
			<main className={`native-entry-layout ${device === 'desktop' ? 'has-qr' : ''}`}>
				<section className="native-entry-copy">
					<p className="native-entry-eyebrow">YOUR EVENT. YOUR CONNECTIONS.</p>
					<h1>ScanDeck<br />Lead Capture</h1>
					<p className="native-entry-intro">Every conversation.<br />Ready for what comes next.</p>
					<p className="native-entry-description">Scan badges and save your leads in the ScanDeck app.</p>
					{code && <div className="native-entry-code">
						<span>Your booth code</span><strong>{code}</strong>
						<p>Use this code and the PIN from your organizer to sign in.</p>
					</div>}
					<div className="native-entry-actions">
						{nativeReady && <a className="native-entry-primary" href={nativeEntryLink(code, true)}
							onClick={() => setRedirect(null)}>Open ScanDeck</a>}
						<div className="native-entry-badges">
							{device !== 'android' && <StoreBadge store="ios" height={52} />}
							{device !== 'ios' && <StoreBadge store="android" height={52} />}
						</div>
					</div>
					{redirect && <p className="native-entry-countdown" role="status">
						Opening {device === 'ios' ? 'the App Store' : 'Google Play'} shortly.{' '}
						<button onClick={() => setRedirect(null)}>Stay here</button>
					</p>}
					<p className="native-entry-instructions">
						{code ? 'After installing, return to this link or enter your booth code in the app.'
							: 'After installing, sign in with the booth code and PIN from your organizer.'}
						{nativeReady && ' Already installed? Tap Open ScanDeck.'}
					</p>
				</section>
				{device === 'desktop' && <aside className="native-entry-qr">
					<p className="native-entry-eyebrow">TAKE IT TO YOUR PHONE</p>
					<div className="native-entry-qr-image">
						{qr ? <img src={qr} alt="Scan to open ScanDeck on your phone" width="240" height="240" />
							: <p role="status">{qrFailed ? 'Use a store link to get the app.' : 'Preparing your QR code…'}</p>}
					</div>
					<h2>Scan. Install. Connect.</h2>
					<p>Point your phone camera at this code to open ScanDeck or find your app store.</p>
					<a href={link} referrerPolicy="no-referrer" rel="noreferrer">{link}</a>
				</aside>}
			</main>
			<footer className="native-entry-footer">
				<p className="native-entry-footer-tagline">SCAN. CAPTURE. CONNECT.</p>
				<p>Need a hand? <a href="mailto:info@scandeckapp.com">info@scandeckapp.com</a></p>
				<p className="native-entry-footer-links">
					<a href="https://scandeckapp.com/privacy/">Privacy</a> &middot; <a href="https://scandeckapp.com/terms/">Terms</a> &middot; <a href="https://scandeckapp.com/contact/">Contact</a>
				</p>
				<p className="native-entry-footer-meta">&copy; 2026 ScanDeck LLC. All rights reserved.</p>
				<p className="native-entry-footer-marks">Apple, the App Store, iPhone and iPad are trademarks of Apple Inc. Google Play and Android are trademarks of Google LLC. ScanDeck is not affiliated with or endorsed by either company.</p>
			</footer>
		</div>
	);
}
